import { Calendar, ChevronDown } from 'lucide-react'
import { motion, AnimatePresence } from 'framer-motion'
import { Button } from '@/components/ui/Button'
import { AiModeButton } from '@/components/ui/AiModeButton'
import { cn } from '@/utils/cn'
import { getDashboardVariants } from '@/components/Dashboard/dashboardMotion'
import { isRocketInstance } from '@/config/instanceProfile'
import type { DashboardPeriod } from '@/hooks/useDashboardData'

const periods: DashboardPeriod[] = ['Esta semana', 'Este mês', 'Último trimestre', 'Este ano', 'Últimos 12 meses']

interface DashboardToolbarActionsProps {
  period: DashboardPeriod
  showPeriods: boolean
  setShowPeriods: (v: boolean | ((prev: boolean) => boolean)) => void
  setPeriod: (p: DashboardPeriod) => void
  onOpenAi: () => void
}

export function DashboardToolbarActions({
  period,
  showPeriods,
  setShowPeriods,
  setPeriod,
  onOpenAi,
}: DashboardToolbarActionsProps) {
  const v = getDashboardVariants(false)

  return (
    <motion.div
      variants={v.container}
      initial='hidden'
      animate='show'
      className='relative flex items-center gap-2'
    >
      <motion.div variants={v.item} className='relative'>
        <Button
          variant='secondary'
          size='sm'
          icon={<Calendar size={13} />}
          onClick={() => setShowPeriods((prev) => !prev)}
        >
          {period}
          <ChevronDown size={12} />
        </Button>
        <AnimatePresence>
          {showPeriods && (
            <motion.div
              initial={{ opacity: 0, y: -6, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -4, scale: 0.98 }}
              transition={{ duration: 0.2 }}
              className={cn(
                'absolute right-0 top-full mt-1 z-50 w-44',
                'bg-[var(--bg-card)] border border-[var(--glass-border)]',
                'rounded-lg shadow-xl overflow-hidden',
              )}
            >
              {periods.map((p, i) => (
                <motion.button
                  key={p}
                  type='button'
                  initial={{ opacity: 0, x: -6 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.04 }}
                  onClick={() => {
                    setPeriod(p)
                    setShowPeriods(false)
                  }}
                  className={cn(
                    'w-full text-left px-3 py-2 text-[13px]',
                    'hover:bg-[var(--glass-bg)] transition-colors',
                    p === period
                      ? 'text-[var(--brand-primary)]'
                      : 'text-[var(--text-secondary)]',
                  )}
                >
                  {p}
                </motion.button>
              ))}
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
      {!isRocketInstance && (
        <motion.div variants={v.item}>
          <AiModeButton onClick={onOpenAi} />
        </motion.div>
      )}
    </motion.div>
  )
}
